import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { preventEnterSubmit } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { captureError } from '@/lib/sentry';

const passwordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type PasswordFormValues = z.infer<typeof passwordSchema>;

export function AuthCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, _session) => {
      console.log('[AuthCallback] Auth event:', event);

      if (event === 'PASSWORD_RECOVERY') {
        // User clicked password reset link - show password reset form
        setIsRecoveryMode(true);
      } else if (event === 'SIGNED_IN') {
        // Don't redirect if we're in recovery mode - user still needs to set password
        if (isRecoveryMode) return;

        toast({
          title: "Successfully signed in",
          description: "Welcome back!",
        });

        // Check for pending invitation redirect. Accept both legacy absolute
        // URLs (older sessionStorage values) and current relative paths, but
        // only honor paths that target the invitation flow to avoid an
        // open-redirect via a poisoned sessionStorage value.
        const pendingRedirect = sessionStorage.getItem('pendingInvitationRedirect');
        if (pendingRedirect) {
          sessionStorage.removeItem('pendingInvitationRedirect');
          let invitationPath: string | null = null;
          if (pendingRedirect.startsWith('/accept-invitation')) {
            invitationPath = pendingRedirect;
          } else {
            try {
              const parsed = new URL(pendingRedirect);
              if (parsed.pathname.startsWith('/accept-invitation')) {
                invitationPath = parsed.pathname + parsed.search;
              }
            } catch {
              // not a URL — ignore
            }
          }

          if (invitationPath) {
            navigate(invitationPath);
          } else {
            navigate('/contacts');
          }
        } else {
          navigate('/contacts');
        }
      }
    });

    // Also check the URL hash for recovery type (fallback detection)
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setIsRecoveryMode(true);
    }

    return () => subscription.unsubscribe();
  }, [navigate, toast, isRecoveryMode]);

  const onSubmit = async (data: PasswordFormValues) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been reset successfully.",
      });

      // Navigate to the app
      navigate('/contacts');
    } catch (error: any) {
      console.error('[AuthCallback] Password update error:', error);
      captureError(error as Error, { feature: 'auth-callback' });
      toast({
        title: "Error",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show password reset form if in recovery mode
  if (isRecoveryMode) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Reset your password</CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={preventEnterSubmit} className="space-y-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="Enter new password"
                          {...field}
                          autoFocus
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="Confirm new password"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating password...
                    </>
                  ) : (
                    'Set new password'
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Default loading state
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}